import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ErrorScreenMessage from '../../../../components/ErrorScreenMessage/ErrorScreenMessage';
import { colors } from '../../../../theme/color-palette';
import PropTypes from 'prop-types';
import { Typography, Box, CircularProgress } from '@material-ui/core';
import { useMediaQuery } from '@mui/material';
import {
	TOTAL,
	ONHAND_INVENTORY,
	ON_ORDER_HYPHEN,
	WAREHOUSE,
	INTRANSIT,
	INLINE_BLOCK,
	WOS_SUMMARY_KEY,
	WOS_TAB_KEY_STORE,
	WOS_TAB_KEY_DC,
} from '../../../../constants/constant';
import ToggleFilter from '../../../../components/Filter/ToggleFilter';
import GenerateTabs from '../../GenerateTabs';
import LineChart from '../LineChart';
import DownloadTrend from '../../../../components/Download/DownloadTrend';
import { convertToTwoDecimal } from '../../../../helper/convertAmount';
import DefinitionDrawer, {
	DefinitionContext,
} from '../../../../components/Definition/index';

const { FONT_COLOR, PURPLE } = colors;

const useStyles = makeStyles(() => ({
	loaderContainer: {
		height: '422px',
		display: 'flex',
	},
	circularProgress: {
		margin: 'auto',
	},
	toggle: {
		marginRight: '16px',
		display: 'flex',
	},
	chartHeader: {
		width: '100%',
		height: '25.02px',
		padding: '16px 0px 4px 16px',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	chartTitle: {
		display: INLINE_BLOCK,
		textAlign: 'left',
		fontSize: (P) => (P.matches ? '16px' : '18px'),
		color: FONT_COLOR,
		fontFamily: 'Bogle-Regular',
		cursor: 'pointer',
	},
	download: {
		marginTop: '-15%',
	},
}));

const formatDataForExcel = (data) => {
	const formattedData = [];
	if (!data) {
		return formattedData;
	}

	for (let idx = 0; idx < data.ON_HAND?.data.length; idx++) {
		formattedData.push({
			wmYrWkNbr: data.ON_HAND?.data[idx].wmYrWkNbr,
			wkStartDt: data.ON_HAND.data[idx]?.wkStartDt,
			wkEndDt: data.ON_HAND.data[idx]?.wkEndDt,
			excel_total_wos: data?.TOTAL ? data?.TOTAL.data[idx].wos : null,
			excel_onHand_wos: data?.ON_HAND ? data?.ON_HAND.data[idx].wos : null,
			excel_onOrder_wos: data?.ON_ORDER ? data?.ON_ORDER.data[idx].wos : null,
			excel_inWhse_wos: data?.IN_WHSE ? data?.IN_WHSE.data[idx].wos : null,
			excel_inTransit_wos: data?.IN_TRANSIT
				? data?.IN_TRANSIT.data[idx].wos
				: null,
		});
	}

	return formattedData;
};

const WeekOfSupplyCard = (props) => {
	const {
		data,
		showToggle,
		title,
		errorMsg,
		weekFilter,
		category,
		type,
		isLoading,
		itemNum,
		itemName,
		selectedTab,
	} = props;
	const [tabValue, setTabValue] = useState(0);
	const [selectedBtn, setSelectedBtn] = useState('Current');
	const options = ['Current', 'Forecast'];
	const matches = useMediaQuery('(max-width:768px)');
	const classes = useStyles({ matches });

	const initialCxt = {
		isDrawerOpened: false,
		metricType: '',
	};
	const [definition, setDefinition] = useState(initialCxt);

	const cxtValue = React.useMemo(
		() => ({ definition, setDefinition }),
		[definition]
	);

	function handleChange(value) {
		setSelectedBtn(value);
	}
	const isColumn =
		weekFilter < 2 && data?.ON_HAND?.data?.length === 1 ? true : false;
	const isUnit = false;
	const chartValue = [
		{
			value: 'wos',
			name: 'WOS trend',
			chartLine: '0',
			color: PURPLE,
			chartType: isColumn ? 'Column' : 'Line',
			isUnit,
		},
	];
	const xAxisData = {
		date: 'wmWkNbr',
		nonDate: 'wmYrWkNbr',
	};

	const WOSTabs = {
		Store: [
			{
				tabName: ONHAND_INVENTORY,
				tabValue: data?.ON_HAND
					? `${convertToTwoDecimal(
							data?.ON_HAND?.summary[WOS_SUMMARY_KEY]
					  )} week`
					: 'NA',
				active: tabValue === 0 ? true : false,
			},
			{
				tabName: ON_ORDER_HYPHEN,
				tabValue: data?.ON_ORDER
					? `${convertToTwoDecimal(
							data?.ON_ORDER?.summary[WOS_SUMMARY_KEY]
					  )} week`
					: 'NA',
				active: tabValue === 1 ? true : false,
			},
			{
				tabName: WAREHOUSE,
				tabValue: data?.IN_WHSE
					? `${convertToTwoDecimal(
							data?.IN_WHSE?.summary[WOS_SUMMARY_KEY]
					  )} week`
					: 'NA',
				active: tabValue === 2 ? true : false,
			},
			{
				tabName: INTRANSIT,
				tabValue: data?.IN_TRANSIT
					? `${convertToTwoDecimal(
							data?.IN_TRANSIT?.summary[WOS_SUMMARY_KEY]
					  )} week`
					: 'NA',
				active: tabValue === 3 ? true : false,
			},
		],
		DC: [
			{
				tabName: TOTAL,
				tabValue: data?.TOTAL
					? `${convertToTwoDecimal(data?.TOTAL?.summary[WOS_SUMMARY_KEY])} week`
					: 'NA',
				active: tabValue === 0 ? true : false,
			},
			{
				tabName: ONHAND_INVENTORY,
				tabValue: data?.ON_HAND
					? `${convertToTwoDecimal(
							data?.ON_HAND?.summary[WOS_SUMMARY_KEY]
					  )} week`
					: 'NA',
				active: tabValue === 1 ? true : false,
			},
			{
				tabName: ON_ORDER_HYPHEN,
				tabValue: data?.ON_ORDER
					? `${convertToTwoDecimal(
							data?.ON_ORDER?.summary[WOS_SUMMARY_KEY]
					  )} week`
					: 'NA',
				active: tabValue === 2 ? true : false,
			},
		],
	};

	let chartData = [];
	if (data && type === 'DC') {
		chartData = data[WOS_TAB_KEY_DC[tabValue]]?.data;
	} else if (data) {
		chartData = data[WOS_TAB_KEY_STORE[tabValue]]?.data;
	} else {
		chartData = [];
	}

	const myComponent = (
		<LineChart
			chartData={chartData}
			chartValue={chartValue}
			xAxisData={xAxisData}
			chartName={title}
			chartWidth={'100%'}
			chartHeight={'320px'}
			weekFilter={weekFilter}
			showExploreButton={false}
			isCompositChart={false}
			isReplenishment={false}
			isWOS={true}
			tabName={WOSTabs[type || 'DC'][tabValue].tabName}
			yAxisMinValue={0}
		/>
	);

	const onTitleClickHandler = () => {
		setDefinition({
			isDrawerOpened: true,
			metricType: 'wos',
		});
	};

	const content = !data ? (
		<ErrorScreenMessage errorMsg={errorMsg} chartTitle={title} />
	) : (
		<>
			<Box className={classes.chartHeader}>
				<Typography
					className={classes.chartTitle}
					onClick={onTitleClickHandler}>
					{title}
				</Typography>
				<Box className={classes.toggle}>
					{showToggle && (
						<Typography>
							<ToggleFilter
								options={options}
								selectedBtn={selectedBtn}
								onChange={handleChange}
							/>
						</Typography>
					)}

					<Typography className={classes.download}>
						<DownloadTrend
							data={formatDataForExcel(data)}
							chartTitle={title}
							weekFilter={weekFilter}
							category={category}
							selectedFilter={'OMNI'}
							inventory={false}
							isTabsTrend={true}
							isWeekOfSupply={true}
							itemNum={itemNum}
							item={itemName}
							hideTabs={false}
							selectedTab={selectedTab}
						/>
					</Typography>
				</Box>
			</Box>
			<GenerateTabs
				createTabs={WOSTabs[type]}
				childComponent={myComponent}
				isInventory={true}
				setTabValue={setTabValue}
				tabValue={tabValue}
				type={type}
			/>
		</>
	);

	return (
		<>
			{isLoading ? (
				<Box className={classes.loaderContainer}>
					<CircularProgress className={classes.circularProgress} />
				</Box>
			) : (
				<>{content}</>
			)}
			<DefinitionContext.Provider value={cxtValue}>
				<DefinitionDrawer
					isOpen={definition.isDrawerOpened}
					metricType={definition.metricType}
					onClose={() => setDefinition(initialCxt)}
				/>
			</DefinitionContext.Provider>
		</>
	);
};

WeekOfSupplyCard.propTypes = {
	data: PropTypes.element.isRequired,
	showToggle: PropTypes.bool,
	title: PropTypes.string,
	errorMsg: PropTypes.string,
	weekFilter: PropTypes.number.isRequired,
	category: PropTypes.string,
	type: PropTypes.string,
	isLoading: PropTypes.bool,
	itemNum: PropTypes.string,
	itemName: PropTypes.string,
	selectedTab: PropTypes.selectedTab,
};

export default WeekOfSupplyCard;
